/**
 * SystemDateDisplay
 *
 * Compact single-line component shown inline with page titles.
 *
 * Business day:     "Fecha de sistema: 20/04/2026"  (green date)
 * Non-business day: "Fecha de sistema: 18/04/2026  [Día no hábil — solo lectura]"
 *                   (amber date + amber pill badge)
 *
 * Shows real CLT calendar date on non-business days (not the systemDate, which is
 * always a business day value). Renders nothing while loading.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSystemDate } from '../../hooks/useSystemDate';

interface SystemDateDisplayProps {
  clientId: string | undefined;
  className?: string;
  style?: React.CSSProperties;
  /** Pre-fetched system date data. When provided, skips the internal useSystemDate fetch. */
  systemDateData?: {
    loading: boolean;
    systemDateDisplay?: string;
    realDateDisplay?: string;
    isBusinessDay?: boolean;
  };
}

const SystemDateDisplay: React.FC<SystemDateDisplayProps> = ({ clientId, className, style, systemDateData }) => {
  const { t } = useTranslation();
  const fetched = useSystemDate(systemDateData ? undefined : clientId);
  const { loading, systemDateDisplay, realDateDisplay, isBusinessDay } = systemDateData || fetched;

  if (loading || !systemDateDisplay) {
    return null;
  }

  if (isBusinessDay) {
    return (
      <div
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '13px',
          ...style,
        }}
      >
        <span style={{ color: '#fff', fontSize: '14px' }}>{t('systemDate.label')}:</span>
        <span style={{ color: '#4caf50', fontWeight: 500, fontSize: '14px' }}>{systemDateDisplay}</span>
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
        ...style,
      }}
    >
      <span style={{ color: '#fff', fontSize: '14px' }}>{t('systemDate.label')}:</span>
      <span style={{ color: '#ffaa00', fontWeight: 500, fontSize: '14px' }}>{realDateDisplay}</span>
      <span style={{
        color: '#ffaa00',
        backgroundColor: 'rgba(255, 170, 0, 0.12)',
        border: '1px solid rgba(255, 170, 0, 0.35)',
        borderRadius: '4px',
        padding: '1px 7px',
        fontSize: '12px',
        whiteSpace: 'nowrap',
      }}>
        {t('systemDate.nonBusinessDay')} — {t('systemDate.readOnlyMode')}
      </span>
    </div>
  );
};

export default SystemDateDisplay;
